/**
 * US 13.7 — the tasks behind one of Mochi's answers, each opening the task.
 * Only tasks the person can open ever reach this card; a blocker in a Hub
 * they can't open shows its key alone.
 */
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";
import { cn } from "@weco/ui";
import type { MochiLang, TaskBrief } from "./api";

export interface TasksListData {
  card: "tasks_list";
  lang: MochiLang;
  tasks: TaskBrief[];
  blockers?: { key: string; title: string | null; id: string | null; restricted: boolean; blocks: string }[];
  count: number;
  message?: string;
}

export function TasksListCard({ data }: { data: TasksListData }) {
  const { i18n } = useTranslation();
  const t = useMemo(() => i18n.getFixedT(data.lang === "ar" ? "ar" : "en"), [i18n, data.lang]);
  const navigate = useNavigate();
  const open = (id: string | null) => id && navigate(`/tasks/t/${id}`);
  if (!data.tasks.length && !data.blockers?.length) return null;
  return (
    <section dir={data.lang === "ar" ? "rtl" : "ltr"} lang={data.lang} className="rounded-2xl border border-border bg-background p-2" aria-label={t("tasks.mochi.tasksFound", { count: data.count, defaultValue: "{{count}} tasks" })}>
      <ul className="divide-y divide-border">
        {(data.blockers ?? []).map((b) => (
          <li key={b.key}>
            <button type="button" disabled={!b.id} onClick={() => open(b.id)} className="w-full flex items-center gap-2 px-2 py-2 text-start text-sm hover:bg-muted/50 rounded-lg disabled:hover:bg-transparent">
              {b.restricted && <Lock className="size-3.5 text-muted-foreground" />}
              <span className="text-xs font-bold text-muted-foreground">{b.key}</span>
              <span className="flex-1 truncate">{b.restricted ? t("tasks.deps.restricted", { defaultValue: "Restricted task" }) : b.title}</span>
              <span className="text-[11px] text-muted-foreground">{t("tasks.mochi.blocks", { key: b.blocks, defaultValue: "blocks {{key}}" })}</span>
            </button>
          </li>
        ))}
        {data.tasks.map((task) => (
          <li key={task.id}>
            <button type="button" onClick={() => open(task.id)} className="w-full flex items-center gap-2 px-2 py-2 text-start text-sm hover:bg-muted/50 rounded-lg">
              <span className="text-xs font-bold text-muted-foreground">{task.key}</span>
              <span className={cn("flex-1 truncate", task.done && "line-through text-muted-foreground")}>{task.title}</span>
              {task.assignees[0] && <span className="text-[11px] text-muted-foreground truncate max-w-24">{task.assignees[0].name}</span>}
              {task.due_date && <span className="text-[11px] text-muted-foreground">{task.due_date}</span>}
            </button>
          </li>
        ))}
      </ul>
      {data.count > data.tasks.length && data.tasks.length > 0 && (
        <p className="px-2 pt-1 text-[11px] text-muted-foreground">{t("tasks.mochi.more", { count: data.count - data.tasks.length, defaultValue: "and {{count}} more" })}</p>
      )}
    </section>
  );
}
